import { Suspense } from 'react';
import { Navigate, Routes as RouterRoutes, Route, useLocation } from 'react-router-dom';
import { lazyWithChunkReload } from './utils/lazyWithChunkReload';
import { NotFoundPage } from './pages/NotFoundPage';

const AuthProviderRoute = lazyWithChunkReload(() => import('./components/auth/AuthProviderRoute'), 'AuthProviderRoute');
const ProtectedRoute = lazyWithChunkReload(() => import('./components/auth/ProtectedRoute'), 'ProtectedRoute');
const ProjectList = lazyWithChunkReload(() => import('./components/ProjectList').then((m) => ({ default: m.ProjectList })), 'ProjectList');
const ProjectView = lazyWithChunkReload(() => import('./components/ProjectView').then((m) => ({ default: m.ProjectView })), 'ProjectView');
const CreditPurchase = lazyWithChunkReload(() => import('./components/CreditPurchase').then((m) => ({ default: m.CreditPurchase })), 'CreditPurchase');
const CreditPurchaseSuccess = lazyWithChunkReload(() => import('./components/CreditPurchaseSuccess').then((m) => ({ default: m.CreditPurchaseSuccess })), 'CreditPurchaseSuccess');
const BeatLibrary = lazyWithChunkReload(() => import('./components/beat-library/BeatLibraryPage'), 'BeatLibrary');
const BeatBanditWall = lazyWithChunkReload(() => import('./components/landing/BeatBanditWall'), 'BeatBanditWall');
const LandingPage = lazyWithChunkReload(() => import('./pages/LandingPage'), 'LandingPage');
const AuthPage = lazyWithChunkReload(() => import('./pages/AuthPage'), 'AuthPage');
const ResetPasswordPage = lazyWithChunkReload(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage');
const AuthCallback = lazyWithChunkReload(() => import('./components/auth/AuthCallback'), 'AuthCallback');
const AccountPage = lazyWithChunkReload(() => import('./pages/AccountPage').then((m) => ({ default: m.AccountPage })), 'AccountPage');
const ExportDownloadPage = lazyWithChunkReload(() => import('./pages/ExportDownloadPage'), 'ExportDownloadPage');
const AdminRoute = lazyWithChunkReload(() => import('./components/admin/AdminRoute').then((m) => ({ default: m.AdminRoute })), 'AdminRoute');
const AdminDashboard = lazyWithChunkReload(() => import('./components/admin/AdminDashboard').then((m) => ({ default: m.AdminDashboard })), 'AdminDashboard');
const PrivacyPolicy = lazyWithChunkReload(() => import('./components/legal/PrivacyPolicy'), 'PrivacyPolicy');
const TermsOfService = lazyWithChunkReload(() => import('./components/legal/TermsOfService'), 'TermsOfService');
const CookiePolicy = lazyWithChunkReload(() => import('./components/legal/CookiePolicy'), 'CookiePolicy');
const ReturnPolicy = lazyWithChunkReload(() => import('./components/legal/ReturnPolicy'), 'ReturnPolicy');
const InvitationPage = lazyWithChunkReload(() => import('./pages/InvitationPage').then((m) => ({ default: m.InvitationPage })), 'InvitationPage');
const PricingPage = lazyWithChunkReload(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })), 'PricingPage');
const ProjectInvitePage = lazyWithChunkReload(() => import('./pages/ProjectInvitePage').then((m) => ({ default: m.ProjectInvitePage })), 'ProjectInvitePage');
const OAuthConsentPage = lazyWithChunkReload(() => import('./pages/OAuthConsentPage'), 'OAuthConsentPage');
const McpDocsApp = lazyWithChunkReload(() => import('./pages/mcp-docs/McpDocsApp'), 'McpDocsApp');
const WritePage = lazyWithChunkReload(() => import('./pages/marketing/WritePage'), 'WritePage');
const ProducePage = lazyWithChunkReload(() => import('./pages/marketing/ProducePage'), 'ProducePage');
const SamplesPage = lazyWithChunkReload(() => import('./pages/marketing/SamplesPage'), 'SamplesPage');

/** /features became /write; keep old #anchors working. */
function FeaturesRedirect() {
  const { hash } = useLocation();
  return <Navigate replace to={{ pathname: '/write', hash }} />;
}

const RouteLoadingFallback = () => (
  <div style={{ minHeight: '40vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    Loading...
  </div>
);

export function Routes() {
  return (
    <Suspense fallback={<RouteLoadingFallback />}>
      <RouterRoutes>
        {/* Routes that need Supabase auth state */}
        <Route element={<AuthProviderRoute />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/debug/corkboard" element={<BeatBanditWall />} />
          <Route path="/sign-in" element={<AuthPage view="sign_in" />} />
          <Route path="/sign-up" element={<AuthPage view="sign_up" />} />
          <Route path="/auth/callback" element={<AuthCallback />} />
          <Route path="/oauth/consent" element={<OAuthConsentPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/project-invite/:token" element={<ProjectInvitePage />} />
          <Route path="/exports/:exportId" element={<ExportDownloadPage />} />
          <Route path="/pricing" element={<PricingPage />} />

          {/* Protected routes (for authenticated users) */}
          <Route element={<ProtectedRoute />}>
            <Route path="/dashboard" element={<ProjectList />} />
            <Route path="/projects/:projectName" element={<ProjectView />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path="/credits" element={<CreditPurchase />} />
            <Route path="/credits/success" element={<CreditPurchaseSuccess />} />
            <Route path="/payment/success" element={<CreditPurchaseSuccess />} />

            {/* Admin routes (admin access only) - moved inside ProtectedRoute */}
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
          </Route>
        </Route>

        {/* Public routes without app auth state */}
        <Route path="/write" element={<WritePage />} />
        <Route path="/produce" element={<ProducePage />} />
        <Route path="/features" element={<FeaturesRedirect />} />
        <Route path="/samples" element={<SamplesPage />} />
        <Route path="/library" element={<BeatLibrary />} />
        <Route path="/library/beat/:beatId" element={<BeatLibrary />} />
        <Route path="/library/:movieId" element={<BeatLibrary />} />
        <Route path="/invite/:token" element={<InvitationPage />} />

        {/* MCP docs site \u2014 public developer reference for the /api/mcp
            server. Subtree owned by src/pages/mcp-docs; it renders inside
            the shared MarketingLayout shell, so no additional auth or
            analytics plumbing is needed here. */}
        <Route path="/mcp/*" element={<McpDocsApp />} />

        {/* Legal pages */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/return-policy" element={<ReturnPolicy />} />

        <Route path="*" element={<NotFoundPage />} />

      </RouterRoutes>
    </Suspense>
  );
}
